import { Suspense, useEffect, useRef, useState } from 'react'
import React from 'react'
import {
  getCustomProfile,
  putCustomProfile,
} from '../../../api/CustomProfilesAPIService'
import MainNavMenu from '../../../menus/MainNavMenu/MainNavMenu'
import { useDispatch, useSelector } from 'react-redux'
import { updateContactDetails } from '../../Users/UserSlice'
import {
  ISubscriptionInfo,
  UpdatePassword,
  cancelSubscription,
  fetchUserDetails,
  resubscribe,
  subscriptionInfo,
} from '../../../services/UserApiService'
import { RootState } from '../../../store'
import { toastType } from '../../Toast/Toast'
import useErrorStatus from '../../../hooks/UseErrorStatus'
import ConfirmDialog from '../../ConfirmDialog'
import { TWS_API_URL, TWS_V15_URL } from '../../../environment/apis.config'
import { getAccessToken, getUser } from '../../../utils/auth.util'
import { useAuth0 } from '@auth0/auth0-react'
import { GRANDSKY_ORG_ID, AEROX_ORG_ID } from '../../../constants/OrganizationIds'

const isResellerOrg = (org_id: string) => {
  return org_id === GRANDSKY_ORG_ID || org_id === AEROX_ORG_ID;
}
import { ErrorTimeouts } from '../../../constants/ErrorTimeouts'

const CustomUnitsComponent = React.lazy(() => import('./CustomUnitsComponent'))

function validateString(s: string): boolean {
  // Check the length of the string
  if (s.length < 8) {
    return false
  }

  // Check for at least 1 uppercase letter
  if (!/[A-Z]/.test(s)) {
    return false
  }

  // Check for at least 1 lowercase letter
  if (!/[a-z]/.test(s)) {
    return false
  }

  // Check for at least 1 special character
  if (!/[\W_]/.test(s)) {
    return false
  }

  // Check for at least 1 number
  if (!/[0-9]/.test(s)) {
    return false
  }

  return true
}

function UserProfile() {
  const [isLoading, setIsLoading] = useState(true)
  const [userFirstName, setUserFirstName] = useState('')
  const [userLastName, setUserLastName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [reseller, setReseller] = useState('')
  const [userMobile, setUserMobile] = useState('')
  const [activeTab, setActiveTab] = useState<'user' | 'subscription' | 'units'>(
    'user'
  )

  const dispatch = useDispatch()
  const clientid = useSelector((state: RootState) => state.user.id)
  const [editUserSettings, setEditUserSettings] = useState(false)

  const [isConfirmSubscriptionDlgOpen, setIsConfirmSubscriptionDlgOpen] =
    useState(false)
  const [showTrialEndMsg, setShowTrialEndMsg] = useState(false)

  const oldPasswordRef = useRef<HTMLInputElement>(null)
  const newPasswordRef = useRef<HTMLInputElement>(null)
  const confirmNewPasswordRef = useRef<HTMLInputElement>(null)
  const today = new Date()
  const [expirationDate, setExpirationDate] = useState(today)
  const [isExpired, setIsExpired] = useState<boolean>(false)
  const [cancelAt, setCancelAt] = useState<string | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [showResubConfirmDlg, setShowResubConfirmDlg] = useState(false)
  const subscriptionDetails: ISubscriptionInfo = {
    productName: null,
    price: null,
    date: null,
    period: null,
    trial_active: null,
    cancel_at_period_end: null,
    isActive: null,
    cancelAt: null,
  }

  const { isAuthenticated } = useAuth0()
  const [userId, setUserId] = useState(getUser()?.sub)
  const user = useSelector((state: RootState) => state.user)
  const statusError = useErrorStatus()
  const productnameRef = useRef<HTMLInputElement>(null)
  const [notActive, setNotActive] = useState<boolean>(false)
  const priceRef = useRef<HTMLInputElement>(null)
  const dateRef = useRef<HTMLInputElement>(null)
  const periodRef = useRef<HTMLInputElement>(null)
  const trial_activeRef = useRef<HTMLInputElement>(null)
  const [cancel_at_period_end, setCancel_at_period_end] =
    useState<boolean>(false)

  const [userSubscriptionDetails, setUserSubscriptionDetails] = useState({
    productName: null,
    price: null,
    date: null,
    period: null,
    trial_active: null,
    cancel_at_period_end: null,
    isActive: null,
    cancelAt: null,
  })

  //fetches subscription info and custom profile details
  const fetchApiDetails = () => {
    const customprofile = getCustomProfile(true)
    customprofile
      .then((res) => {
        const units = res?.customProfile?.units
        if (units && units.temperature) setTemperatureUnits(units?.temperature)
        if (units && units.wind_speed) setWindSpeedUnits(units?.wind_speed)
        if (units && units.precipitationRate)
          setPrecipRateUnits(units?.precipitationRate)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
      })
    if (editUserSettings) {
      statusError.addMessage(
        'User contact details updated successfully',
        200,
        'success',
        false,
        ErrorTimeouts.USER_PROFILE
      )
      setEditUserSettings(false)
    }
    subscriptionInfo()
      .then((res) => {
        if (res !== undefined) {
          setUserSubscriptionDetails({
            productName: res.productName,
            price: res.price,
            date: res.date,
            period: res.period,
            trial_active: res.trial_active,
            cancel_at_period_end: res.cancel_at_period_end,
            isActive: res.isActive,
            cancelAt: res.cancelAt,
          })

          setCancelAt(res.cancelAt)
          setIsActive(res.isActive !== null ? res.isActive : false)
          setCancel_at_period_end(
            res.cancel_at_period_end !== null ? res.cancel_at_period_end : false
          )
          if (!res.trial_active && isExpired) {
            setShowTrialEndMsg(true)
          }
          if (trial_activeRef.current) {
            trial_activeRef.current.value =
              res.trial_active !== null ? res.trial_active.toString() : 'false'
          }
          if (priceRef.current) {
            priceRef.current.value =
              res.price !== null ? res.price.toString() : '0.00'
          }
          if (dateRef.current) {
            const dt = new Date(res.date !== null ? res.date.toString() : today)
            dateRef.current.value = dt.toLocaleDateString()
            setExpirationDate(dt)
            if (dt < today) {
              setIsExpired(true)
            } else {
              setIsExpired(false)
            }
          }
          if (periodRef.current) {
            periodRef.current.value =
              res.period !== null ? res.period.toString() : 'none'
          }
          if (productnameRef.current) {
            productnameRef.current.value =
              res.productName !== null ? res.productName : 'none'
          }
        }
      })
      .catch((error) => {
        setIsActive(false)
        statusError.addMessage(
          "Currently you don't have an active subscription.",
          400,
          'error',
          false,
          ErrorTimeouts.USER_PROFILE
        )
      })
  }

  useEffect(() => {
    if (isAuthenticated && getUser()) {
      setUserId(getUser()?.sub)
      fetchApiDetails()
    }
  }, [isAuthenticated, getUser()])

  useEffect(() => {
    if (user) {
      setUserFirstName(user.firstname !== null ? user.firstname : '')
      setUserLastName(user.lastname !== null ? user.lastname : '')
      setUserEmail(user.email !== null ? user.email : '')
      setUserMobile(user.primary !== null ? user.primary : '')
      setReseller(user.reseller !== null ? user.reseller.reseller : '')
    }
  }, [user])

  useEffect(() => {
    if (newPasswordRef.current !== null) {
      // adding the new-password attribute to the password
      // this doesn't seem to work for me, but the council of wise elders say to do it.
      newPasswordRef.current.setAttribute('autocomplete', 'new-password')
    }
    if (confirmNewPasswordRef.current !== null) {
      confirmNewPasswordRef.current.setAttribute('autocomplete', 'new-password')
    }
    if (oldPasswordRef.current !== null) {
      oldPasswordRef.current.setAttribute('autocomplete', 'new-password')
    }
  }, [])

  const handleCancelSubButton = () => {
    if (subscriptionDetails && !subscriptionDetails.trial_active) {
      window.open(
        'http://billing.stripe.com/p/login/8x29AU8I33UMdzs843dMI00',
        '_blank'
      )
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('userId')
    localStorage.setItem('logout-event', 'logout' + Math.random())
    setNotActive(true)
    setTimeout(() => {
      window.location.href = TWS_V15_URL
    }, 5000)
  }

  const CancelSubscription = () => {
    const result = cancelSubscription()
    result
      .then((res) => {
        setIsConfirmSubscriptionDlgOpen(false)
        setCancel_at_period_end(true)
        statusError.addMessage(
          'We’re sorry to see you go.  Please continue to visit TruWeatherSolutions.com to keep up with our latest innovations.',
          200,
          'success',
          true,
          ErrorTimeouts.USER_PROFILE
        )
      })
      .catch((err) => {
        statusError.addMessage(
          'Problem cancelling subscription.',
          200,
          'error',
          false,
          ErrorTimeouts.USER_PROFILE
        )
      })
  }

  const handleResubscribeButton = () => {
    setShowResubConfirmDlg(true)
  }

  const doResubscribe = () => {
    const result = resubscribe()
    result
      .then((res) => {
        setShowResubConfirmDlg(false)
        statusError.addMessage(
          'Your subscription has been re-enabled.',
          200,
          'success',
          false,
          ErrorTimeouts.USER_PROFILE
        )
        setCancel_at_period_end(true)
      })
      .catch((err) => {
        statusError.addMessage(
          'There was a problem resubscribing.',
          200,
          'error',
          false,
          ErrorTimeouts.USER_PROFILE
        )
      })
  }

  const handleChangePassword = async () => {
    const pwdChangeUrl = `${TWS_API_URL}/auth/password-change`
    const token = await getAccessToken()

    fetch(pwdChangeUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.ticket) {
          window.location.href = data.ticket
          // window.open(data.ticket, '_blank', 'noopener,noreferrer');
        } else {
          // TODO: Show error here
          console.log('Some error')
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }

 

  const editUser = () => {
    dispatch(
      updateContactDetails({
        userId: userId,
        firstname: userFirstName,
        lastname: userLastName,
        // email: userEmail,
        primary: userMobile,
      })
    )
  }

  const Loader = () => <div>Loading...</div>

  return (
    <div className="h-screen flex flex-col justify-center items-center w-full  overflow-y-auto">
      <ConfirmDialog
        isOpen={isConfirmSubscriptionDlgOpen}
        onConfirm={CancelSubscription}
        onCancel={() => {
          setIsConfirmSubscriptionDlgOpen(false)
        }}
        message="Are you sure you want to cancel your subscription?"
      />
      <ConfirmDialog
        isOpen={showResubConfirmDlg}
        onConfirm={doResubscribe}
        onCancel={() => {
          setShowResubConfirmDlg(false)
        }}
        message="Are you sure you want to resubscribe?"
      />

      <div className="self-start">
        <MainNavMenu />
      </div>

      <div className="flex flex-col w-full h-full bg-white px-4 lg:px-12 xl:px-12 text-sm max-w-[1080px]">
        {/* Tabs Header */}
        <div className="flex border-b border-gray-200">
          <button
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 ${
              activeTab === 'user'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('user')}
          >
            User Settings
          </button>
          {/* <button
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 ${
              activeTab === 'units'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('units')}
          >
            Custom Units
          </button> */}
          <button
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 ${
              activeTab === 'subscription'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('subscription')}
          >
            Subscription Details
          </button>
        </div>

        {/* Tabs Content */}
        <div className="pt-4">
          {activeTab === 'user' && (
            <div>
              <div className="grid grid-cols-2 gap-2  ">
                <div className="">
                  <div className="text-left text-sm mt-2 mb-1">
                    First Name <span className="text-red-600">*</span>
                  </div>
                  <div className="text-left">
                    <input
                      className={`border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 ${
                        !editUserSettings
                          ? 'bg-white'
                          : 'dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400'
                      }`}
                      value={userFirstName}
                      disabled={!editUserSettings}
                      onChange={(e) => setUserFirstName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="">
                  <div className="text-left text-sm mt-2 mb-1">
                    Last Name <span className="text-red-600">*</span>
                  </div>
                  <div className="text-left">
                    <input
                      className={`border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 ${
                        !editUserSettings
                          ? 'bg-white'
                          : 'dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400'
                      }`}
                      value={userLastName}
                      disabled={!editUserSettings}
                      onChange={(e) => setUserLastName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="">
                  <div className="text-left text-sm mt-2 mb-1">
                    Email <span className="text-red-600">*</span>
                  </div>
                  <div className="text-left">
                    <input
                      className={`border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 ${
                        !editUserSettings
                          ? 'bg-white'
                          : 'dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400'
                      }`}
                      value={userEmail}
                      disabled={true}
                      onChange={(e) => setUserEmail(e.target.value)}
                    />
                  </div>
                </div>
                <div className="">
                  <div className="text-left text-sm mt-2 mb-1">
                    Mobile <span className="text-red-600">*</span>
                  </div>
                  <div className="text-left">
                    <input
                      className={`border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 ${
                        !editUserSettings
                          ? 'bg-white'
                          : 'dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400'
                      }`}
                      value={userMobile}
                      disabled={!editUserSettings}
                      onChange={(e) => setUserMobile(e.target.value)}
                    />
                  </div>
                </div>

                <div className="text-left mt-[-0.5rem]">
                  {editUserSettings ? (
                    <div className="flex ">
                      <div className="py-2.5 mr-12 mt-4 text-center text-white bg-green-500 rounded-md w-24 h-10">
                        <button
                          onClick={() => {
                            editUser()
                          }}
                        >
                          SAVE
                        </button>
                      </div>
                      <div className="py-2.5 my-0 mt-4 text-center text-white bg-green-500 rounded-md w-24 h-10">
                        <button
                          onClick={() => {
                            setEditUserSettings(false)
                          }}
                        >
                          CANCEL
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="py-2.5 my-0 mt-4 text-center text-white bg-green-500 rounded-md w-24 h-10">
                      <button
                        onClick={() => {
                          setEditUserSettings(true)
                        }}
                      >
                        EDIT
                      </button>
                    </div>
                  )}
                </div>
                {getUser()?.isDatabaseUser ? (
                  <button
                    onClick={handleChangePassword}
                    className="py-2.5 my-0 mt-4 text-center text-white bg-green-500 rounded-md w-40 h-10"
                  >
                    Change Password
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>

          {activeTab === 'units' && (
            <Suspense fallback={<Loader />}><CustomUnitsComponent /></Suspense>
          )}

        {activeTab === 'subscription' && (
          <div>
            <div className="grid grid-cols-1 gap-2  ">
              {showTrialEndMsg && (
                <div className="col-span-2">
                  <strong>
                    Your TruWeather Solutions V360 Trial Has Concluded - Keep
                    Your Missions Flying High!
                  </strong>
                  <p className="my-3">
                    It looks like your trial period with TruWeather Solutions
                    V360 has ended. We hope you've seen clear skies ahead with
                    our precision meteorological insights.
                  </p>
                  <p>
                    Don’t let unpredictable weather ground your missions. By
                    upgrading to a full subscription, ensure continuous access
                    to the real-time data and forecasts necessary for safe,
                    efficient flights.
                  </p>
                  <button
                    type="button"
                    className="py-2.5 my-0 mt-4 text-center text-white bg-green-500 rounded-md w-56 h-10"
                    onClick={() => {
                      window.location.href = `${TWS_API_URL}/payment?token=${localStorage.getItem(
                        'token'
                      )}`
                    }}
                  >
                    SEE SUBSCRIPTION OPTIONS
                  </button>
                </div>
              )}
              {!showTrialEndMsg && !isActive && (
                <div className="col-span-2">
                  <strong>
                    Your TruWeather Solutions V360 Subscription{' '}
                    {isExpired ? 'has ended ' : 'is not active '} - Keep Your
                    Missions Flying High!
                  </strong>
                  <p className="my-3">
                    It looks like your subscription with TruWeather Solutions
                    V360 {isExpired ? 'has ended' : 'is not active'}. We hope
                    you've seen clear skies ahead with our precision
                    meteorological insights.
                  </p>
                  <p>
                    Don’t let unpredictable weather ground your missions.{' '}
                    {isExpired
                      ? 'By resubscribing, '
                      : 'By purchasing a subscription, '}{' '}
                    ensure continuous access to the real-time data and forecasts
                    necessary for safe, efficient flights.
                  </p>
                  <button
                    type="button"
                    className="py-2.5 my-0 mt-4 text-center text-white bg-green-500 rounded-md w-56 h-10"
                    onClick={() => {
                      window.location.href = `${TWS_API_URL}/payment?token=${localStorage.getItem(
                        'token'
                      )}`
                    }}
                  >
                    SEE SUBSCRIPTION OPTIONS
                  </button>
                </div>
              )}
              {!showTrialEndMsg && isActive && (
                <div className="flex flex-col w-full">
                  <div className="w-full bg-white rounded-2xl p-6 space-y-4">
                    {[
                      {
                        label: 'Product Name',
                        value: userSubscriptionDetails.productName,
                      },
                      {
                        label: 'Price',
                        value: '$' + userSubscriptionDetails.price,
                      },
                      {
                        label: 'Expiration Date',
                        value: new Date(
                          userSubscriptionDetails?.date
                        ).toLocaleDateString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                        }),
                      },
                      {
                        label: 'Trial Access',
                        value: userSubscriptionDetails.trial_active
                          ? 'Yes'
                          : 'No',
                      },
                      {
                        label: 'Billing Cycle',
                        value:
                          userSubscriptionDetails.period === 'year'
                            ? 'Annual'
                            : 'Monthly',
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                      >
                        <span className="font-semibold text-gray-600">
                          {item.label}
                        </span>
                        <span className="text-gray-900 font-medium">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="text-left mt-4 p-6">
                    <button
                      onClick={handleCancelSubButton}
                      type="button"
                      className="py-2.5 text-center text-white bg-green-500 rounded-md h-10 px-6"
                    >
                      Manage Subscription
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserProfile
